import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { PlayerCardHeaderRow } from './styles';
import UDText from 'ud-ui/components/text';
import { TeamPlayerAvatar } from '../../../team-card/styles';
import UDButton from 'ud-ui/components/button';
import { differenceInYears, format } from 'date-fns';
import { deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import { DivProps } from '../../../../../../../typings';
import { useNavigate } from 'react-router-dom';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useModal } from 'ud-ui/components/modal-old/use-modal';
import { playersIsLoadingSelector } from 'modules/teams/store/players/selectors';
import { AppDispatch } from 'store/store';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { Team } from 'modules/teams/domain/interfaces/team';

type PlayerCardHeaderProps = {
  teamId: number;
  player: TeamPlayer;
  containerProps?: Omit<DivProps, 'children'>;
  index: number;
};

const PlayerCardHeader = (props: PlayerCardHeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { open, close, Modal } = useModal({ defaultShow: false });
  const isLoading = useSelector(playersIsLoadingSelector);
  const queryClient = useQueryClient();

  const { teamId, player, containerProps, index } = props;

  const name = [player.lastName, player.firstName, player.middleName].filter(Boolean).join(' ');
  const birthDate = player.birthDate ? new Date(player.birthDate) : null;
  const birthDateString = birthDate ? format(birthDate, 'dd.MM.yyyy') : '';
  const years = birthDate ? differenceInYears(new Date(), birthDate) : null;
  const yearsString = years ? t('ages', { count: years }) : '';
  const fullBirthDate = [birthDateString, yearsString].filter(Boolean).join(', ') || '-';

  const handleKick = useCallback(async () => {
    try {
      await dispatch(deleteTournamentPlayerApplication({ userId: player.userId, teamId })).unwrap();

      queryClient.setQueryData<Team>(['team', teamId], (old) => {
        if (!old) return old as any;
        return {
          ...old,
          players: old.players.filter((p) => p.userId !== player.userId),
        };
      });

      toast.success('Игрок успешно исключен');
      close();
    } catch (e) {
      toast.error('Не удалось исключить игрока');
    }
  }, [dispatch, player.userId, teamId, queryClient, close]);

  const handleClickEdit = useCallback(() => {
    navigate(Pages.PLAYERS.builders.view(player.userId), { state: { fromTeam: teamId } });
  }, [navigate, player.userId, teamId]);

  return (
    <PlayerCardHeaderRow {...containerProps} className={classNames(containerProps?.className)} onClick={() => {}}>
      <div className="player-card">
        <div className="row">
          {index !== undefined ? (
            <UDText type="subhead" className="player-column bold col-1">
              {index + 1}
            </UDText>
          ) : (
            ''
          )}

          <div className="player-column col-4">
            <TeamPlayerAvatar url={player?.avatar ?? USER_DEFAULT_AVATAR} className="player-avatar" size={40} square />
            <UDText type="subhead" className="bold">
              {name}
            </UDText>
          </div>

          <UDText type="subhead" className="player-column col-2" style={{ flexBasis: '15%' }}>
            {fullBirthDate}
          </UDText>

          <div className="player-column col-5 d-flex justify-content-end">
            <UDButton
              className="d-flex align-items-center mr-3"
              variant="primary"
              onClick={() => {
                handleClickEdit();
              }}
            >
              <span>Редактировать</span>
            </UDButton>

            <UDButton className="d-flex align-items-center" variant="secondary" onClick={open}>
              <span>Исключить</span>
            </UDButton>
          </div>
        </div>
      </div>

      <Modal>
        <UDText type="headline" className="color-SurfaceRomanSilver30 px-10" style={{ textAlign: 'center' }}>
          Вы действительно хотите исключить игрока
        </UDText>
        <UDText type="headline" className="color-SurfaceRomanSilver30 mb-15 px-10" style={{ textAlign: 'center' }}>
          {player.firstName} {player.lastName} {player.middleName ?? ''}?
        </UDText>
        <div className={'d-flex w-100'} style={{ gap: 10 }}>
          <UDButton
            disabled={isLoading}
            className="d-flex align-items-center justify-content-center w-50 mb-3"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
          >
            <span>Нет</span>
          </UDButton>
          <UDButton
            disabled={isLoading}
            loading={isLoading}
            className="d-flex align-items-center justify-content-center w-50 mb-3"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleKick();
            }}
          >
            <span>Да</span>
          </UDButton>
        </div>
      </Modal>
    </PlayerCardHeaderRow>
  );
};

export default PlayerCardHeader;
